/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["variables"] }] */
/* eslint no-underscore-dangle: ["error", { "allow": ["_meta"] }] */
import { React } from '@biletiniz/ui-core';
import { NextSeo, FAQPageJsonLd } from '@biletiniz/seo';
import { Layout } from '@biletiniz/ui-layout';
import { HomePage } from '@biletiniz/ui-home-template';
import Router from 'next/router';
import { qs } from '@biletiniz/shared';
import { FlightSearchFormContainer } from '@biletiniz/web-app-containers';
import { useTheme } from '@biletiniz/ui-style';
import Head from 'next/head';
import {
  popularRoutesFlights,
  popularRoutesBus,
  homePageFAQ,
  getCleanFaqJsonid,
} from '@biletiniz/web-app-utils';

import { LayoutHeader } from '@biletiniz/web-app-components';
import { useI18n } from '@biletiniz/intl';
import { useRouter, useHotBlogs } from '../src/hooks';

const Index = ({ hotBlogs }) => {
  const { flightOffersSearchForm, router } = useRouter();
  
  const theme = useTheme();

  const pt = theme.space[5] - 4;
  const spt = theme.space[5] - 15;
  const i18n = useI18n();

  return (
    <>
      <NextSeo
        title={
          i18n.activeLocale === 'en'
            ? 'Bus & Flight Tickets in Turkey | Book Online | Biletiniz.com'
            : 'En Ucuz Otobüs ve Uçak Bileti | Biletiniz.com | Vize Danışmanlığı'
        }
        description={
          i18n.activeLocale === 'en'
            ? 'Compare bus and flight tickets across Turkey in one place. Book with a TÜRSAB-licensed travel agency, secure card payment and support in English.'
            : 'Biletiniz.com ile en uygun otobüs ve uçak biletleri bulmanın yanı sıra vize başvurusu ve danışmanlık hizmetlerinden de faydalanabilirsiniz. Uygun fiyatlar, kolay rezervasyon!'
        }
        canonical={`https://biletiniz.com${
          router.locale !== 'tr' ? '/en' : ''
        }`}
      />
      <Head>
        <meta
          name="keywords"
          content={
            i18n.activeLocale === 'en'
              ? 'bus tickets turkey, flight tickets turkey, turkey travel booking, istanbul bus tickets, domestic flights turkey, book bus turkey'
              : 'Otobüs Bileti, Uçak Bileti, En Ucuz Bilet, En Uygun Bilet, Ucuz Otobüs Bileti, Uygun Otobüs Bileti, Ucuz Uçak Bileti, Uygun Uçak Bileti, Vize Başvurusu, Vize Danışmanlığı'
          }
        />
        <meta
          name="facebook-domain-verification"
          content="thjgnx9fmlkqf4k5q3z03kepoopgq8"
        />
      </Head>
      <FAQPageJsonLd
        mainEntity={homePageFAQ[`${i18n.activeLocale}`]?.map(item => ({
          questionName: getCleanFaqJsonid(item?.question),
          acceptedAnswerText: getCleanFaqJsonid(item?.answer),
        }))}
      />
      <Layout>
        <LayoutHeader router={router} withMobileNav={true} />
        <Layout.Container width="full">
          <HomePage>
            <HomePage.Search
              pt={[`${theme.space[4] + 10}px`, spt, 4, pt, pt]}
              showInfo
            >
              <FlightSearchFormContainer
                initialValues={flightOffersSearchForm}
                fieldWidth={['100%', 1 / 2, 1 / 2, 1 / 3, 1 / 4]}
                hasBoxShadow={true}
                mode="important"
                variant="home-page"
                onSubmit={variables => {
                  Router.push(`/ucak-seferler?${qs.stringify(variables)}`);
                }}
                mustCompress={true}
              />
            </HomePage.Search>
            <HomePage.Companies />
            <HomePage.Travels />
            {i18n.activeLocale === 'en' ? null : (
              <HomePage.Articles
                cards={hotBlogs.map(item => {
                  const hotBlog = item?.node;
                  return {
                    title: hotBlog?.title?.[0].text,
                    description: hotBlog?.description?.[0].text,
                    href: `/content/blogs/${hotBlog?._meta?.uid}`,
                    imgSrc: hotBlog?.coverimage?.url,
                    imgAlt: hotBlog?.coverimage?.alt,
                  };
                })}
              />
            )}
            <HomePage.SeoArticles />
            <HomePage.AppDownload />
            <HomePage.PopularRoutes
              popularRoutesBus={popularRoutesBus}
              popularRoutesFlights={popularRoutesFlights}
            />
            <HomePage.SeoArticlesVisa />
            <HomePage.Features />
            <HomePage.Faq homePageFaq={homePageFAQ} />
          </HomePage>
        </Layout.Container>
        <Layout.Footer />
      </Layout>
    </>
  );
};

export async function getStaticProps(context) {
  const { default: lngDict = {} } = await import(
    `@biletiniz/intl/locales/${context.locale}.json`
  );
  const data = await useHotBlogs();

  return {
    props: { lng: context.locale, lngDict, hotBlogs: data.hotBlogs },
  };
}

export default Index;
